/**
 * Login Page
 * User authentication with Google OAuth, 2FA, and Passkeys
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Allowed external domains for returnTo redirect (security measure)
const ALLOWED_DOMAINS = [
  'presentation-grades-fqh6ifxsva-uc.a.run.app',
  'grading.rpi.plotka.me',
  'grading-staging.rpi.plotka.me',
  'localhost'
];

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [searchParams] = useSearchParams();

  const { login, register, verify2FA, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  // Check for external returnTo parameter (from grading app, etc.)
  const returnTo = searchParams.get('returnTo');

  // If user is already authenticated and there's a returnTo, redirect immediately
  useEffect(() => {
    if (authLoading) return; // Wait for auth to load

    if (user && returnTo) {
      const decoded = decodeURIComponent(returnTo);

      // Same-origin relative path — redirect directly
      if (decoded.startsWith('/') && !decoded.startsWith('//')) {
        window.location.href = decoded;
        return;
      }

      // External URL — check allowed domains
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const returnUrl = new URL(decoded);
          if (ALLOWED_DOMAINS.some(domain => returnUrl.hostname === domain || returnUrl.hostname.endsWith('.' + domain))) {
            const separator = returnUrl.search ? '&' : '?';
            window.location.href = `${decoded}${separator}token=${token}`;
            return;
          }
        } catch (e) {
          console.error('Invalid returnTo URL:', e);
        }
      }
    }
  }, [user, authLoading, returnTo]);

  // Handle OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    const status = searchParams.get('status');
    const error = searchParams.get('error');

    if (token) {
      // Save token to localStorage
      localStorage.setItem('token', token);

      // Check if there's a returnTo URL
      if (returnTo) {
        const decoded = decodeURIComponent(returnTo);

        // Same-origin relative path (e.g., /t/) — redirect directly
        if (decoded.startsWith('/') && !decoded.startsWith('//')) {
          window.location.href = decoded;
          return;
        }

        // External URL — check allowed domains
        try {
          const returnUrl = new URL(decoded);
          if (ALLOWED_DOMAINS.some(domain => returnUrl.hostname === domain || returnUrl.hostname.endsWith('.' + domain))) {
            const separator = returnUrl.search ? '&' : '?';
            window.location.href = `${decoded}${separator}token=${token}`;
            return;
          }
        } catch (e) {
          console.error('Invalid returnTo URL:', e);
        }
      }

      // Default: Reload the page to trigger AuthContext to load user
      window.location.href = from;
    } else if (status === 'pending') {
      toast.warning('Your account is pending approval. Please wait for an administrator to approve your account.');
    } else if (status === 'rejected') {
      toast.error('Your account registration was rejected.');
    } else if (error) {
      toast.error('OAuth authentication failed. Please try again.');
    }
  }, [searchParams, from, navigate, returnTo]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let result;
    if (isLogin) {
      result = await login(formData.email, formData.password);
      
      // Check if 2FA is required
      if (result.requiresTwoFactor) {
        setTempToken(result.tempToken);
        setShow2FA(true);
        setLoading(false);
        return;
      }
    } else {
      result = await register(formData.name, formData.email, formData.password);
    }

    setLoading(false);

    // Handle registration pending status
    if (!isLogin && result.success && result.pending) {
      // Don't navigate, just show success message
      return;
    }

    if (result.success) {
      // Small delay to ensure state is fully updated before navigation
      setTimeout(async () => {
        try {
          // Check for redirect
          if (returnTo) {
            const decoded = decodeURIComponent(returnTo);
            if (decoded.startsWith('/') && !decoded.startsWith('//')) {
              window.location.href = decoded;
              return;
            }
            const token = localStorage.getItem('token');
            try {
              const returnUrl = new URL(decoded);
              if (ALLOWED_DOMAINS.some(domain => returnUrl.hostname === domain || returnUrl.hostname.endsWith('.' + domain))) {
                const separator = returnUrl.search ? '&' : '?';
                window.location.href = `${decoded}${separator}token=${token}`;
                return;
              }
            } catch (e) {
              console.error('Invalid returnTo URL:', e);
            }
          }

          // Get user info and redirect to their landing page
          const response = await api.get('/auth/me');
          const userData = response.data.user;

          // Use user's landingPage setting, fallback to role-based default
          let destination = userData.landingPage || '/';

          // If user was trying to access a specific page, respect that unless they're landing on a default page
          if (from !== '/' && userData.role !== 'admin' && userData.role !== 'student') {
            destination = from;
          }

          navigate(destination, { replace: true });
        } catch (error) {
          console.error('Redirect error:', error);
          navigate(from, { replace: true });
        }
      }, 200);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await verify2FA(tempToken, twoFactorCode);

    setLoading(false);

    if (result.success) {
      setTimeout(async () => {
        try {
          // Check for redirect
          if (returnTo) {
            const decoded = decodeURIComponent(returnTo);
            if (decoded.startsWith('/') && !decoded.startsWith('//')) {
              window.location.href = decoded;
              return;
            }
            const token = localStorage.getItem('token');
            try {
              const returnUrl = new URL(decoded);
              if (ALLOWED_DOMAINS.some(domain => returnUrl.hostname === domain || returnUrl.hostname.endsWith('.' + domain))) {
                const separator = returnUrl.search ? '&' : '?';
                window.location.href = `${decoded}${separator}token=${token}`;
                return;
              }
            } catch (e) {
              console.error('Invalid returnTo URL:', e);
            }
          }

          const response = await api.get('/auth/me');
          const userData = response.data.user;
          let destination = userData.landingPage || '/';

          if (from !== '/' && userData.role !== 'admin' && userData.role !== 'student') {
            destination = from;
          }

          navigate(destination, { replace: true });
        } catch (error) {
          console.error('Redirect error:', error);
          navigate(from, { replace: true });
        }
      }, 200);
    }
  };

  const handleGoogleLogin = () => {
    // For OAuth, we need the full backend URL since window.location.href doesn't use the proxy
    // In production, use the current origin (same domain as frontend)
    // In development, use REACT_APP_API_URL or localhost
    const backendUrl = process.env.NODE_ENV === 'production'
      ? window.location.origin
      : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
    // Pass returnTo parameter if present so it survives the OAuth flow
    const googleUrl = returnTo
      ? `${backendUrl}/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`
      : `${backendUrl}/api/auth/google`;
    window.location.href = googleUrl;
  };

  // 2FA Modal
  if (show2FA) {
    return (
      <LoginContainer>
        <Container>
          <LoginCard
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Logo>rplotka</Logo>
            <Title>Two-Factor Authentication</Title>
            <Subtitle>Enter the code from your authenticator app</Subtitle>

            <Form onSubmit={handle2FASubmit}>
              <FormGroup>
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  type="text"
                  id="code"
                  name="code"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  required
                  placeholder="000000"
                  maxLength="6"
                  pattern="\d{6}"
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify'}
              </SubmitButton>
            </Form>

            <ToggleText>
              <ToggleLink onClick={() => setShow2FA(false)}>
                Back to login
              </ToggleLink>
            </ToggleText>
          </LoginCard>
        </Container>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <Container>
        <LoginCard
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Logo>rplotka</Logo>
          <Title>{isLogin ? 'Welcome Back' : 'Create Account'}</Title>
          <Subtitle>
            {isLogin
              ? 'Sign in to access your account'
              : 'Register to get started'}
          </Subtitle>

          <Form onSubmit={handleSubmit}>
            {!isLogin && (
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!isLogin}
                  placeholder="John Doe"
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your.email@example.com"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="••••••••"
                minLength="6"
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Sign Up'}
            </SubmitButton>
          </Form>

          <Divider>
            <DividerLine />
            <DividerText>or</DividerText>
            <DividerLine />
          </Divider>

          <GoogleButton type="button" onClick={handleGoogleLogin}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </GoogleButton>

          <ToggleText>
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <ToggleLink onClick={() => setIsLogin(!isLogin)}>
              {isLogin ? 'Sign Up' : 'Sign In'}
            </ToggleLink>
          </ToggleText>

          <InfoBox>
            <InfoTitle>New Authentication Features</InfoTitle>
            <InfoText>
              {isLogin ? (
                <>
                  • Sign in with Google OAuth<br />
                  • Two-factor authentication support<br />
                  • Passkey/WebAuthn support (coming soon)
                </>
              ) : (
                <>
                  • New registrations require admin approval<br />
                  • You'll be notified once your account is approved<br />
                  • You can also register with Google
                </>
              )}
            </InfoText>
          </InfoBox>
        </LoginCard>
      </Container>
    </LoginContainer>
  );
};

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 3rem 0;
`;

const Container = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
`;

const LoginCard = styled.div`
  background: var(--bg-secondary);
  padding: 3rem;
  border-radius: 1.5rem;
  box-shadow: var(--shadow-xl);
`;

const Logo = styled.div`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--text-secondary);
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ToggleText = styled.p`
  text-align: center;
  color: var(--text-secondary);
`;

const ToggleLink = styled.span`
  color: var(--accent-primary);
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: var(--border-color);
`;

const DividerText = styled.span`
  color: var(--text-tertiary);
  font-size: 0.875rem;
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: white;
  color: #1f1f1f;
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;

  &:hover {
    background: #f8f8f8;
    border-color: #4285F4;
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  svg {
    flex-shrink: 0;
  }
`;

const InfoBox = styled.div`
  background: var(--bg-primary);
  padding: 1.5rem;
  border-radius: 0.75rem;
  border-left: 4px solid var(--accent-primary);
  margin-top: 2rem;
`;

const InfoTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const InfoText = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

export default Login;
